import { Column, Index, PrimaryGeneratedColumn } from 'typeorm';
import { Entity } from '../../decorators';

/**
 * Lifecycle of one pipeline row (a stage row, or the run header row).
 *
 * PD-14995 adds two end states:
 * - `stopped`: an operator asked the run to stop (config app, or a signal to
 *   matchmaker-cli) and it stopped itself at its next check. The header row
 *   records who, when and why.
 * - `abandoned`: the run's process went away without finishing (crash, kill,
 *   a laptop asleep past the heartbeat window). A later run reclaimed its
 *   lock and marked the header, and any stage row still reading `running`,
 *   with this status so they no longer look live.
 */
export type LicensePipelineStageStatus =
	| 'running'
	| 'completed'
	| 'failed'
	| 'skipped'
	| 'stopped'
	| 'abandoned';

/**
 * Ordered stages of the nightly per-tenant licensing pipeline (PD-14439).
 * `match` → `prune` → `auto_confirm` → `stats_refresh`; a stage that is not
 * enabled for a run is still recorded as `skipped` so the log always shows
 * the full picture.
 */
export type LicensePipelineStage =
	// Fills client_license.license_number_normalized where the ERP sync left it
	// NULL. First, so the scan and the confirms it feeds look licenses up by a
	// key that exists.
	| 'license_number_normalize'
	| 'match'
	| 'prune'
	| 'auto_confirm'
	// PD-17231: close pending suggestions to a not-in-force registration for a
	// ship-to slot that already holds a confirmed in-force license. After
	// auto-confirm, whose confirms can newly fill a slot.
	| 'in_force_slot_supersede'
	// Copies the verified lm_registry values onto confirmed-matched
	// client_license rows. It is a stage rather than its own schedule because
	// the matchmaker CANNOT do this work — it skips every client whose licenses
	// are all linked — and because a separate cron silently stopped for five
	// days without anyone noticing (PD-15247).
	| 'registry_reapply'
	// Link drift (2026-09-05): act on regulator links that have ENDED since a
	// confirm — flag human confirms, auto-unlink cascade confirms.
	| 'link_drift'
	// Facility personnel: rebuild facility_personnel (the license Overview
	// "Personnel" box) from live, state-published links on confirmed facility
	// matches — after the stages that confirm or unlink a license.
	| 'link_individuals'
	// Auto-sunset (2026-09-20): record a `board_action_sunset` activity for
	// every Adverse Action item whose board actions have all ended, so What
	// Workd Did counts real events instead of deriving a figure from end dates.
	// Runs before stats_refresh so the tile sees this run's events.
	| 'board_action_sunset'
	| 'stats_refresh';

/**
 * PD-14995 — the stage value of a run's HEADER row: one per run, written
 * before stage 0, carrying the run mutex (`activeLockKey`), the heartbeat and
 * any stop request. Stage readers filter by stage name, so the header never
 * reads as a stage.
 */
export const LICENSE_PIPELINE_RUN_HEADER_STAGE = 'run';

/** Every value the `stage` column holds: the stages, plus the run header. */
export type LicensePipelineRowStage =
	| LicensePipelineStage
	| typeof LICENSE_PIPELINE_RUN_HEADER_STAGE;

/**
 * Durable per-stage run history for the nightly licensing pipeline
 * (PD-14439/PD-14440). One row per (runKey, stage), written by the
 * crm-api stage-runner as each stage starts and finishes, plus (PD-14995)
 * one header row per run (stage `run`) that holds the run mutex, the
 * heartbeat and any stop request.
 *
 * Lives in the TENANT database on purpose: it is auto-created by the
 * database-sync (additive), writable by the tenant app user with existing
 * grants, and the config-api central stack reads it cross-tenant (with its
 * privileged credential) as the "did everyone finish last night?" doneness
 * check before harvesting. Replaces "console logs + ephemeral BullMQ job
 * progress" as the app-side record of what ran.
 *
 * auditable: false — this IS an ops log; mirroring every insert/update into
 * `_audit` would only duplicate it (same reasoning as
 * license_compliance_snapshot).
 */
@Index('idx_lpr_run_key', ['runKey'])
@Index('idx_lpr_started_at', ['startedAt'])
// PD-14995 — the run mutex. Only a live run's header row carries a lock key,
// and the key is cleared when the run ends, so at most one live run per key
// can exist on a tenant. MySQL allows any number of NULLs in a UNIQUE index.
@Index('uq_lpr_active_lock_key', ['activeLockKey'], { unique: true })
@Entity('license_pipeline_run', {}, { auditable: false })
export class LicensePipelineRunEntity {
	@PrimaryGeneratedColumn({ name: '_id', unsigned: true })
	public id!: number;

	/**
	 * Groups the stage rows of one pipeline run — the BullMQ job id for
	 * queue-driven runs, or a `cli-…` key for matchmaker-cli runs.
	 */
	@Column('varchar', { name: 'runKey', length: 191 })
	public runKey!: string;

	/** Which pipeline produced the run: FULL_SCAN | INCREMENTAL_SCAN. */
	@Column('varchar', { name: 'jobType', length: 64 })
	public jobType!: string;

	@Column('varchar', { name: 'stage', length: 64 })
	public stage!: LicensePipelineRowStage;

	@Column('varchar', { name: 'status', length: 32, default: 'running' })
	public status!: LicensePipelineStageStatus;

	/**
	 * UTC, and compared against the database clock in SQL — both by
	 * `TIMESTAMPDIFF(…, startedAt, NOW())` when reporting how long a run has
	 * taken and by the config-api harvester's `startedAt >= DATE_SUB(NOW(),
	 * INTERVAL 26 HOUR)` doneness check.
	 *
	 * The writer sends a JS `Date`, and mysql2 serialises that from the
	 * *process's* local wall clock, so the value is only comparable with `NOW()`
	 * while the process runs in UTC. Every entry point pins itself, and the
	 * DataSource factory enforces it as a backstop — see
	 * `apps/crm-api/src/shared/utils/process-timezone.ts` (PD-14998).
	 */
	@Column('datetime', {
		name: 'startedAt',
		default: () => 'CURRENT_TIMESTAMP',
	})
	public startedAt!: Date;

	/** UTC, under the same invariant as `startedAt`. */
	@Column('datetime', { name: 'completedAt', nullable: true })
	public completedAt!: Date | null;

	/**
	 * Stage-specific counters, e.g. match `{processed, matched}`, prune
	 * `{scanned, pruneCandidates, pruned, byReason, abortedReason?}`,
	 * auto_confirm `{candidates, confirmed, dryRun}`.
	 */
	@Column('json', { name: 'counts', nullable: true })
	public counts!: Record<string, unknown> | null;

	@Column('text', { name: 'error', nullable: true })
	public error!: string | null;

	// ── PD-14995 run mutex, heartbeat and stop request (header row only) ──
	//
	// Stage rows leave every column below NULL. The lock lives in the tenant
	// database rather than Redis because matchmaker-cli runs against a LOCAL
	// Redis: the database is the only store a queued run and a CLI run share.

	/**
	 * The exclusivity key while the run is live (LICENSE_PIPELINE_LOCK_KEY),
	 * NULL once it ends. The UNIQUE index above is the mutex.
	 */
	@Column('varchar', { name: 'activeLockKey', length: 64, nullable: true })
	public activeLockKey!: string | null;

	/**
	 * Written every 30 seconds by the run's own timer, independent of progress.
	 * A live key whose heartbeat is older than JOB_RUN_STALE_AFTER_SECONDS
	 * belongs to a process that went away, and the next run may reclaim it.
	 * UTC, under the same invariant as `startedAt`.
	 */
	@Column('datetime', { name: 'heartbeatAt', nullable: true })
	public heartbeatAt!: Date | null;

	/** When the run last reported progress (a scan tick, a finished stage). UTC. */
	@Column('datetime', { name: 'lastProgressAt', nullable: true })
	public lastProgressAt!: Date | null;

	/** Which process runs it: `crm-api job <id>` or `cli <pid>@<host>`. */
	@Column('varchar', { name: 'owner', length: 191, nullable: true })
	public owner!: string | null;

	/** Set by the config app's Stop action; the run stops at its next check. UTC. */
	@Column('datetime', { name: 'stopRequestedAt', nullable: true })
	public stopRequestedAt!: Date | null;

	/**
	 * Global Workd user id (`dvp._user._id`) of the associate who asked for
	 * the stop. Global, not tenant-local: the request comes from the config
	 * app, where the actor is a Workd associate with no tenant user row.
	 */
	@Column('int', {
		name: 'stopRequestedByWorkdUserId',
		unsigned: true,
		nullable: true,
	})
	public stopRequestedByWorkdUserId!: number | null;

	/** Display name of whoever asked for the stop, as it read at the time. */
	@Column('varchar', { name: 'stopRequestedByName', length: 191, nullable: true })
	public stopRequestedByName!: string | null;

	/** The reason given with the stop request. */
	@Column('varchar', { name: 'stopReason', length: 500, nullable: true })
	public stopReason!: string | null;
}
